<template>
  <div
    :class="{ 'paginator--blur': scrollTop >= 5 }"
    class="paginator"
    :style="{ width: paginatorWidth + 'px' }"
  >
    <div class="paginator__control">
      <button
        :disabled="!newsFeedStore.currentPage"
        class="paginator-previous__icon paginator__icon"
        :class="{ 'paginator__icon--disabled': currentPage === 1 }"
        @click="prevPage"
      >
        <ArrowBottomIcon class="paginator__chevron paginator__chevron--prev" />
      </button>
      <div class="paginator-current">
        <div class="paginator-current__page">
          <input
            v-model="currentPage"
            type="number"
            :style="{ width: `${Math.max(1, String(currentPage).length)}ch` }"
          />
        </div>
        /
        <div>
          {{ totalPages }}
        </div>
      </div>
      <button
        :disabled="newsFeedStore.totalCount - newsFeedStore.offset < 20"
        class="paginator-next__icon paginator__icon"
        :class="{ 'paginator__icon--disabled': currentPage === totalPages }"
        @click="nextPage"
      >
        <ArrowBottomIcon class="paginator__chevron paginator__chevron--next" />
      </button>
    </div>

    <div class="paginator__info">
      <div class="paginator-total-page">
        <div class="paginator-total-page__number">
          <input
            v-model="newsFeedStore.take"
            type="number"
            :style="{
              width: `${Math.max(
                1,
                String(newsFeedStore.take ?? '').length
              )}ch`,
            }"
            @change="updateItemsPerPage"
          />
        </div>
      </div>

      <div class="paginator__total-message">
        {{ t("news_page_pagination.on_page") }}
        {{ formatCount(newsFeedStore.totalCount) }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { t } from "@/controllerLK/GlobusLocalization";
import { computed, onBeforeUnmount, ref, toRef, watch, defineProps } from "vue";
import { debounce } from "@/functions/debouncer";
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";

const props = defineProps({
  container: {
    type: [Object, null],
    required: true,
  },
});

const newsFeedStore = useNewsFeedStore();
const container = toRef(props, "container");

const formatCount = (value) =>
  String(value ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

// Расчет ширины пагинатора под разный масштаб
const paginatorWidth = ref(0);
let ro;

// Рассчет скролла для наложения блюра на пагинатор
const scrollTop = ref(0);

const onScroll = (e) => {
  const el = e.target || e;
  scrollTop.value = el.scrollTop;
};

watch(
  container,
  (el, prev) => {
    // убираем старый наблюдатель
    if (prev && ro) {
      ro.disconnect();
    }
    // вешаем новый
    if (el) {
      ro = new ResizeObserver(([entry]) => {
        // Округление вверх 2-ух знаков после запятой
        const raw = entry.contentRect.width;
        const roundedUp = Math.ceil(raw * 100) / 100;
        paginatorWidth.value = roundedUp.toFixed(2);
      });
      ro.observe(el);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  ro?.disconnect();
});

watch(
  container,
  (el, prev) => {
    if (prev) prev.removeEventListener("scroll", onScroll);
    if (el) {
      onScroll(el);
      el.addEventListener("scroll", onScroll, { passive: true });
    }
  },
  { immediate: true }
);

// Пагинатор
const totalPages = computed(() =>
  newsFeedStore.take > 0
    ? Math.ceil(newsFeedStore.totalCount / newsFeedStore.take)
    : 1
);

const updateItemsPerPage = () => {
  if (newsFeedStore.take < 1) {
    newsFeedStore.take = 1;
  } else if (newsFeedStore.take > 100) {
    newsFeedStore.take = 100;
  }
  newsFeedStore.currentPage = 0;
  updateNews(newsFeedStore.searchValue);
};

const currentPage = computed({
  get: () => newsFeedStore.currentPage + 1,
  set: (val) => {
    const clamped = Math.max(1, Math.min(val, totalPages.value));
    newsFeedStore.currentPage = clamped - 1;
    updateNews(newsFeedStore.searchValue);
  },
});

const nextPage = () => {
  if (newsFeedStore.currentPage < totalPages.value - 1) {
    newsFeedStore.currentPage++;
    updateNews(newsFeedStore.searchValue);
  }
};

const prevPage = () => {
  if (newsFeedStore.currentPage > 0) {
    newsFeedStore.currentPage--;
    updateNews(newsFeedStore.searchValue);
  }
};

const updateNews = debounce(async () => {
  if (container.value) container.value.scrollTop = 0; // Прокручиваем контейнер в начало, чтобы показать первую новость
  await newsFeedStore.getNews({ preserveChosenCards: true });
}, 500);
</script>
<style lang="scss" scoped>
.paginator {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px 10px 8px;
  color: var(--text-text-pur);
  background: #eef1f4;
  gap: 10px;

  &__control {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 8px;
  }

  &__chevron {
    width: 14px;
    height: 14px;

    &--prev {
      transform: rotate(90deg);
    }

    &--next {
      transform: rotate(-90deg);
    }
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: default;
    color: #8d98b3;
  }

  &__total-message {
    font-size: 14px;
    line-height: 1;
    white-space: nowrap;
  }

  &-total-page {
    display: flex;
    align-items: center;

    &__number {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 36px;
      height: 36px;
      padding: 0 10px;
      color: #8d98b3;
      border-radius: 999px;
      background: #fff;

      input {
        min-width: 0;
        padding: 0;
        text-align: center;
        background: transparent;
        color: inherit;
        font-size: 14px;

        &:focus {
          outline: none;
        }
      }

      &:focus-within {
        box-shadow: inset 0 0 0 1px #cacaca;
      }

      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        margin: 0;
        -webkit-appearance: none;
      }

      input[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
      }
    }
  }

  &-current {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: default;
    color: #8d98b3;
    font-size: 14px;
    line-height: 1;

    &__page {
      display: flex;
      align-items: center;
      justify-content: center;
      color: inherit;
      width: 36px;
      height: 36px;

      border-radius: 999px;
      background: #fff;
      color: #8d98b3;

      input {
        min-width: 0;
        padding: 0;
        text-align: center;
        background: transparent;
        color: inherit;
        font-size: 14px;

        &:focus {
          outline: none;
        }
      }

      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        margin: 0;
        -webkit-appearance: none;
      }

      input[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
      }
    }
  }

  &__icon {
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #c3cad6;
    cursor: pointer;
    user-select: none;

    &--disabled {
      color: #e1e5ee;
      cursor: default;
    }
  }

  &-current__page {
    display: flex;
    flex-wrap: nowrap;
  }
}
</style>
